import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath, Route, useParams, useRouteMatch} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {Icon} from 'components/ui-kit/Icon';
import {
    ContentContainer,
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {AUTHENTIC_STATUS_COLORS, PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductsStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {commonDataState$, Gate} from 'models/products/detail';
import {colors} from 'theme';
import {AggregationStorageLocation, UserPermission} from 'types';
import {ProductCommonRaw, ProductFormatted} from 'types/products-detail';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {formatAddress} from 'utils/formatAddress';
import {handleNil} from 'utils/handleNil';
import {fromHex, toHex} from 'utils/string';

import {Link} from '../../ui-kit/Link';

import {AggregateContent} from './AggregateContent';
import {CommonInfo} from './CommonInfo';
import {Operations} from './Operations';
import {CustomLink} from './styled';
import {Tracking} from './Tracking';

export function convertData(data: ProductCommonRaw | null, t: TFunction): ProductFormatted {
    const result: ProductFormatted = {
        code: handleNil(data?.markingCode),
        fileLink: data?.markingCode
            ? encodeURIComponent(data?.markingCode?.replace('\\u001D', ''))
            : '#',
        name: handleNil(data?.product?.productName),
        brand: handleNil(data?.product?.productTrademark),
        gtin: handleNil(data?.product?.gtin),
        packagingLevel: handleNil(data?.packagingLevel),
        isAggregate: data?.packagingLevel !== 0,
        isGroup: data?.packagingLevel !== 0 && data?.packagingType === 'GROUP',
        status: handleNil(data?.status, (value: string) =>
            t(`dictionaries.products.status.${value}`),
        ),
        generatedTime: '—',
        issuedTime: '—',
        appliedTime: '—',
        aggregationInfo: '—',
        enteredCirculationTime: '—',
        leftCirculationTime: '—',
        storageLocation: handleNil(data?.storageLocation, (value: AggregationStorageLocation) =>
            formatAddress(value, {toDisplay: ['city', 'address'], storage: false}),
        ),
        storageName: handleNil(data?.storageLocation?.storageName),
        authentic: {
            status: (
                <StatusWrapper
                    color={
                        AUTHENTIC_STATUS_COLORS[
                            data?.markingCodeAuthenticInfo?.authenticStatus ?? 'DEFAULT'
                        ]
                    }
                >
                    {data?.markingCodeAuthenticInfo?.authenticStatus
                        ? t(
                              `dictionaries.authenticStatus.${data?.markingCodeAuthenticInfo?.authenticStatus}`,
                          )
                        : '—'}
                </StatusWrapper>
            ),
            date: data?.markingCodeAuthenticInfo?.authenticDetectionDateTime
                ? formatDate(
                      data?.markingCodeAuthenticInfo.authenticDetectionDateTime,
                      'dd.MM.yyyy, HH:mm',
                  )
                : '—',
            type: data?.markingCodeAuthenticInfo?.authenticDetectionContext
                ? t(
                      `pages.products.detail.common.authenticContext.${data?.markingCodeAuthenticInfo?.authenticDetectionContext}`,
                  )
                : '—',
            link: data?.markingCodeAuthenticInfo?.documentId ? (
                <Link to={`/receipts/${data?.markingCodeAuthenticInfo?.documentId}`}>
                    {t('pages.products.detail.common.documentLink')}
                </Link>
            ) : (
                '—'
            ),
        },
    };

    if (data?.statuses) {
        Object.values(data.statuses).forEach((elem) => {
            switch (elem.status) {
                case ProductsStatus.GENERATED:
                    result.generatedTime = formatDate(elem.operationTime, 'dd MMMM yyyy, HH:mm');
                    break;
                case ProductsStatus.ISSUED:
                    result.issuedTime = formatDate(elem.operationTime, 'dd MMMM yyyy, HH:mm');
                    break;
                case ProductsStatus.APPLIED:
                    result.appliedTime = formatDate(elem.operationTime, 'dd MMMM yyyy, HH:mm');
                    break;
                case ProductsStatus.AGGREGATED:
                    result.aggregationInfo = (
                        <>
                            {formatDate(elem.operationTime, 'dd MMMM yyyy, HH:mm')}
                            {elem.parentMarkingCode && (
                                <>
                                    {`, ${t('pages.products.detail.common.inPackage')} `}
                                    <CustomLink
                                        to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                                            id: toHex(`${null}§${elem.parentMarkingCode}`),
                                        })}
                                    >
                                        {elem.parentMarkingCode}
                                    </CustomLink>
                                </>
                            )}
                            {!elem.parentMarkingCode &&
                                data.packagingLevel !== 0 &&
                                `, ${t('pages.products.detail.common.topLevelPackage')}`}
                        </>
                    );
                    break;
                case ProductsStatus.INTRODUCED:
                    result.enteredCirculationTime = formatDate(
                        elem.operationTime,
                        'dd MMMM yyyy, HH:mm',
                    );
                    break;
                case ProductsStatus.SOLD:
                case ProductsStatus.DROPOUT:
                    result.leftCirculationTime = formatDate(
                        elem.operationTime,
                        'dd MMMM yyyy, HH:mm',
                    );
                    break;
            }
        });
    }

    return result;
}

export const ProductsDetailPage = () => {
    const {t} = useTranslation();
    const {path, url} = useRouteMatch();
    const {id: rawId}: {id: string} = useParams();
    const copy = useClipboard();

    const id = fromHex(rawId);
    let gtin: string | null = null;
    let markingCode = 'invalid';

    if (id) {
        const separatorPosition = id.indexOf('§');
        gtin = id.substring(0, separatorPosition);
        gtin = gtin === 'null' ? null : gtin;
        markingCode = id.substring(separatorPosition + 1);
    }

    const {data, error, isLoading} = useStore(commonDataState$);

    const mappedData = React.useMemo(() => convertData(data, t), [data, t]);

    const copyCode = () => {
        copy(markingCode);
        toast.success(t('pages.products.detail.common.markingCodeCopied'));
    };

    return (
        <>
            <Gate gtin={gtin} markingCode={markingCode} />
            <PermissionAccess
                permissions={[
                    UserPermission.PRODUCT_GRID_VIEW,
                    UserPermission.ALL_PRODUCT_GRID_VIEW,
                ]}
            >
                <BackTo to={ROUTE.PRODUCTS_GRID}>{t('pages.products.detail.back')}</BackTo>
            </PermissionAccess>
            <Page title={t('pages.products.detail.common.markingCodes')}>
                <ContentContainer
                    status={isLoading ? 'loading' : error != null ? 'error' : 'default'}
                >
                    <TitleWrapper>
                        <TitleText>
                            <Title>
                                {t('pages.products.detail.common.markingCode')}{' '}
                                <Id>{mappedData.code}</Id>
                            </Title>
                            <CopyButton
                                icon={
                                    <Icon
                                        source={CopyIcon}
                                        width={15}
                                        height={15}
                                        fillColor={colors.brandPrimary}
                                    />
                                }
                                onClick={copyCode}
                                hoverIndication={true}
                                label={t('pages.products.detail.common.copyCode')}
                                labelPlace={'bottom'}
                            />
                        </TitleText>
                        <div style={{marginLeft: 'auto'}}>{data?.owner}</div>
                        <StatusWrapper color={PRODUCT_STATUS_COLORS[data?.status ?? 'DEFAULT']}>
                            {mappedData.status}
                        </StatusWrapper>
                    </TitleWrapper>

                    {/*<ColumnsWrapper>*/}
                    {/*    <Column>*/}
                    {/*        <HeaderTypography>*/}
                    {/*            {t('pages.products.detail.common.sourcePhoto')}:*/}
                    {/*        </HeaderTypography>*/}
                    {/*        <OriginalPhotoModal code={mappedData.code} link={mappedData.fileLink} />*/}
                    {/*    </Column>*/}
                    {/*</ColumnsWrapper>*/}

                    <RouterTabList baseUrl={url}>
                        <RouterTab url="">{t('pages.products.detail.common.commonInfo')}</RouterTab>

                        {(mappedData.isAggregate || mappedData.isGroup) && (
                            <RouterTab url="content">
                                {t(
                                    `pages.products.detail.common.${
                                        mappedData.isGroup ? 'groupContent' : 'aggregateContent'
                                    }`,
                                )}
                            </RouterTab>
                        )}

                        <RouterTab url="operations">
                            {t('pages.products.detail.common.operations')}
                        </RouterTab>

                        <RouterTab url="tracking">
                            {t('pages.products.detail.common.tracing')}
                        </RouterTab>
                    </RouterTabList>

                    <RouterSwitch rootPath={path}>
                        <Route path={`${path}`} exact>
                            <CommonInfo data={mappedData} isLoading={isLoading} />
                        </Route>

                        <Route path={`${path}/content`} exact>
                            <AggregateContent markingCode={markingCode} gtin={gtin} />
                        </Route>

                        <Route path={`${path}/operations`} exact>
                            <Operations />
                        </Route>

                        <Route path={`${path}/tracking`} exact>
                            <Tracking />
                        </Route>
                    </RouterSwitch>
                </ContentContainer>
            </Page>
        </>
    );
};
